import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaidIcon from '@mui/icons-material/Paid';
import LayersIcon from '@mui/icons-material/Layers';

import SellIcon from '@mui/icons-material/Sell';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import WalletSelectModal from '../components/common/WalletSelectModal';
import TermsModal from '../components/common/TermsModal';
import { useWallet } from '../context/WalletContext';
import logo from '../assets/logo.png';
import Roadmap from '../components/Roadmap';
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepConnector,
  Typography,
  
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
   AppBar, Box, Button, Checkbox, Chip,
  Container, CssBaseline, Divider, Drawer, FormControlLabel,
  Grid, IconButton, LinearProgress, Link,
   Stack, ThemeProvider, Toolbar,
   createTheme,
} from '@mui/material';



ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

const theme = createTheme({
  typography: {
    fontFamily: 'Inter, sans-serif',
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: 'rgba(0,26,44,0.85)',
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(11,23,34,0.08)',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 4px rgba(11,23,34,0.06)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          boxShadow: 'none',
          color: '#001a2c',
          '&:hover': { boxShadow: 'none', backgroundColor: '#7dd3fc' },
        },
      },
    },
  },
});
const totalSupply = 1_000_000_000; // 1,000,000,000 DOLP

  const allocations = [
    { label: 'Mining', amount: 950_000_000, pct: 95, color: '#0ea5e9' },
    { label: 'Liquidity', amount: 40_000_000, pct: 4, color: '#003049' },
    { label: 'Marketing', amount: 10_000_000, pct: 1, color: '#94a3b8' },
    { label: 'Owner / Team', amount: 0, pct: 0, color: '#d1d5db' },
  ];

  const pieData = {
    labels: allocations.map((a) => a.label),
    datasets: [
      {
        data: allocations.map((a) => a.pct),
        backgroundColor: allocations.map((a) => a.color),
        borderWidth: 0,
      },
    ],
  };

const navItems = ['Features', 'Tokenomics', 'Roadmap', 'Whitepaper'];

const features = [
  {
    step: '01', icon: <SellIcon color="primary" />,
    title: 'Pre-Sale Distribution',
    description: 'Controlled allocation model with transparent progress and supply visibility.',
  },
  {
    step: '02', icon: <PaidIcon color="primary" />,
    title: 'MLM Reward Engine',
    description: 'Structured referral system with deterministic payout logic and clear levels.',
  },
  {
    step: '03', icon: <LayersIcon color="primary" />,
    title: 'Tiered Mining Core',
    description: '12-tier mining architecture that scales rewards through participation.',
  },
];

const tokenomics = [
  { label: 'Mining Distribution', value: 95 },
  { label: 'Liquidity Pool', value: 4 },
  { label: 'Marketing/Development', value: 1 },
  { label: 'Team Allocation', value: 0 },
];




const presaleProgress = 45;
const SECTION_SX = { px: { xs: 2.5, sm: 3, md: 5, lg: 8 } };

function LandingPage() {
  const { connectWallet, isConnecting, account } = useWallet();
  const navigate = useNavigate();
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const openWalletModalListener = () => setWalletModalOpen(true);
    window.addEventListener('openWalletModal', openWalletModalListener);
    return () => window.removeEventListener('openWalletModal', openWalletModalListener);
  }, []);

  const handleWalletSelect = async (walletType) => {
    setWalletModalOpen(false);
    await connectWallet(walletType);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleConnectClick = () => {
    if (!tosChecked || isConnecting) return;
    setWalletModalOpen(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* ── HERO WRAPPER — dark + world map ── */}
      <Box
        sx={{
          position: 'relative',
          isolation: 'isolate',
          background: 'linear-gradient(160deg, #001a2c 0%, #003049 55%, #004a6e 100%)',
          overflow: 'hidden',
          // World map overlay
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage: `url("https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'min(1400px, 95vw) auto',
            opacity: 0.07,
            filter: 'invert(1)',
            pointerEvents: 'none',
          },
          // Bottom wave fade into page
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            height: 80,
            background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 80'%3E%3Cpath fill='%23f0f4f8' d='M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z'/%3E%3C/svg%3E") no-repeat bottom/cover`,
            pointerEvents: 'none',
            zIndex: 1,
          },
        }}
      >
        {/* NAVBAR */}
        <AppBar position="sticky" elevation={0} color="transparent" sx={{ zIndex: 10 }}>
          <Toolbar sx={{ justifyContent: 'space-between', ...SECTION_SX, py: 0.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box component="img" src={logo} alt="Dolphin" sx={{ width: 36, height: 36, borderRadius: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff' }}>
                DOLPHIN
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {navItems.map((item) => (
                <Button
                  key={item}
                  sx={{ display: { xs: 'none', md: 'inline-flex' }, color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}
                  onClick={() => scrollToSection(item.toLowerCase())}
                >
                  {item}
                </Button>
              ))}
              {account ? (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ ml: 1, backgroundColor: '#0ea5e9', color: '#001a2c' }}
                  onClick={() => navigate('/dashboard')}
                >
                  Dashboard
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1, borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff' }}
                  onClick={() => setWalletModalOpen(true)}
                >
                  Connect Wallet
                </Button>
              )}
              <IconButton sx={{ color: '#ffffff', display: { md: 'none' } }} onClick={() => setMobileMenuOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>

        {/* HERO CONTENT */}
        <Container
          maxWidth={false}
          sx={{
            pt: { xs: 7, md: 9 },
            pb: { xs: 10, md: 12 },
            minHeight: { md: 'calc(100vh - 64px)' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
            ...SECTION_SX,
          }}
        >
          <Grid container spacing={6} alignItems="center">

            {/* LEFT */}
            <Grid item xs={12} md={6}>
              {/* Live badge */}
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 20, px: 1.5, py: 0.6, mb: 2.5,
              }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.25)' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Pre-Sale Live · Polygon
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.6rem', md: '4rem' },
                  lineHeight: 1.0,
                  mb: 2.5,
                  color: '#ffffff',
                }}
              >
                Mine Smarter.
                <br />
                <Box component="span" sx={{ color: '#38bdf8' }}>Grow Community</Box>
                <br />
                Yield.
              </Typography>

              <Typography sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: { xs: '1rem', md: '1.05rem' },
                lineHeight: 1.8,
                mb: 3.5,
                maxWidth: 440,
              }}>
                Audited contracts, deterministic MLM rewards and 12-tier token emissions — built for serious participants.
              </Typography>

              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 4 }}>
                {['Audited Contracts', '95% Community', 'Polygon Native', '12-Tier Mining'].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.7)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                    }}
                    variant="outlined"
                  />
                ))}
              </Stack>

              {/* TOS + CTA */}
              <FormControlLabel
                sx={{ mb: 2, ml: 0 }}
                control={
                  <Checkbox
                    checked={tosChecked}
                    onChange={(e) => setTosChecked(e.target.checked)}
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#38bdf8' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    I agree to the{' '}
                    <Link component="button" underline="hover" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }} onClick={() => setTermsOpen(true)}>
                      Terms of Service
                    </Link>
                  </Typography>
                }
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleConnectClick}
                  disabled={isConnecting || !tosChecked}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: '#0ea5e9',
                    color: '#001a2c',
                    py: 1.5,
                    px: 3.5,
                    fontSize: '1rem',
                    '&:hover': { backgroundColor: '#38bdf8' },
                    '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  {isConnecting ? 'Connecting...' : 'Start Mining'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection('whitepaper')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.25)',
                    color: '#ffffff',
                    py: 1.5,
                    px: 3.5,
                    fontSize: '1rem',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  View Whitepaper
                </Button>
              </Stack>
            </Grid>

            {/* RIGHT — stats */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  { label: 'Total Supply', value: '1B', sub: 'DOLP tokens' },
                  { label: 'Community Allocation', value: '95%', sub: 'of supply' },
                  { label: 'Mining Tiers', value: '12', sub: 'active levels' },
                  { label: 'Pre-Sale Filled', value: `${presaleProgress}%`, sub: 'of target' },
                ].map((stat) => (
                  <Grid item xs={6} key={stat.label}>
                    <Box sx={{
                      p: { xs: 2, md: 2.5 },
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                    }}>
                      <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 800, color: '#ffffff', lineHeight: 1.1, mb: 0.4 }}>
                        {stat.value}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>
                        {stat.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#38bdf8', fontWeight: 600, mt: 0.3 }}>
                        {stat.sub}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Progress */}
              <Box sx={{
                p: 2.5,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.9rem' }}>Pre-Sale Progress</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>{presaleProgress}% of 1B DOLP</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={presaleProgress}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#0ea5e9', borderRadius: 999 },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)' }}>0 DOLP</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)' }}>1,000,000,000</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Bottom strip */}
          <Box sx={{
            mt: { xs: 5, md: 7 },
            pt: 2.5,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 3,
          }}>
            {[
              { label: 'Network', value: 'Polygon' },
              { label: 'Contract', value: 'Audited' },
              { label: 'Referral', value: 'Multi-tier MLM' },
              { label: 'Mining', value: 'Open' },
              { label: 'Standard', value: 'ERC-20' },
              { label: 'Phase', value: 'Live Now' },
            ].map((item) => (
              <Box key={item.label}>
                <Typography sx={{ display: 'block', mb: 0.3, fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── REST OF PAGE ── */}
      <Box sx={{ backgroundColor: '#f0f4f8' }}>

        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{ sx: { width: 280, backgroundColor: '#ffffff' } }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Menu</Typography>
            <Stack spacing={1}>
              {navItems.map((item) => (
                <Button key={item} color="inherit" sx={{ justifyContent: 'flex-start' }} onClick={() => scrollToSection(item.toLowerCase())}>
                  {item}
                </Button>
              ))}
            </Stack>
          </Box>
        </Drawer>

        {/* ── FEATURES ── */}
       {/* ── FEATURES ── */}
<Box
  id="features"
  sx={{
    py: { xs: 10, md: 14 },
    background: 'linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)',
  }}
>
  <Container maxWidth="lg">
    {/* Header */}
    <Box sx={{ mb: 10, textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#0ea5e9',
          mb: 2,
        }}
      >
        How It Works
      </Typography>
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2rem', md: '2.8rem' },
          fontWeight: 'bold',
          mb: 3,
        }}
      >
        Built Different
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ maxWidth: 520, mx: 'auto', lineHeight: 1.8, fontSize: '1rem' }}
      >
        Every layer of the protocol is transparent, audited, and designed for long-term participation.
      </Typography>
    </Box>

    {/* Stepper */}
    <Stepper
      orientation="vertical"
      connector={<StepConnector sx={{ '& .MuiStepConnector-line': { borderColor: '#0ea5e9' } }} />}
    >
      {features.map((feature, index) => (
        <Step key={feature.title} active>
          <StepLabel
            icon={
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: 'rgba(14,165,233,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {feature.icon}
              </Box>
            }
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#0ea5e9',
              }}
            >
              Step {feature.step}
            </Typography>
            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              {feature.title}
            </Typography>
          </StepLabel>
          <StepContent>
            <Typography
              color="text.secondary"
              sx={{ lineHeight: 1.8, fontSize: '0.95rem', mb: 4 }}
            >
              {feature.description}
            </Typography>
          </StepContent>
        </Step>
      ))}
    </Stepper>
  </Container>
</Box>


        {/* ── TOKENOMICS ── */}
        
<Box
  id="tokenomics"
  sx={{
    py: { xs: 10, md: 14 },
    background: 'linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)',
  }}
>
  <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 } }}>
    {/* Header */}
    <Box sx={{ mb: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#0ea5e9',
              mb: 1.5,
            }}
          >
            Token Supply & Allocation
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.9rem', md: '2.8rem' },
              fontWeight: 800,
              mb: 1.5,
              color: 'text.primary',
            }}
          >
            Total Supply: 1,000,000,000 DOLP
          </Typography>

          <Box
            sx={{
              width: 72,
              height: 5,
              backgroundColor: '#0ea5e9',
              borderRadius: 3,
              mx: 'auto',
              mb: 2,
            }}
          />

          <Typography color="text.secondary" sx={{ maxWidth: 820, mx: 'auto', lineHeight: 1.7 }}>
            Distribution is community-first: 95% of all DOLP tokens are allocated to mining distribution.
            There is no founder allocation, no private sale, and no team vesting. This structure eliminates insider
            advantage and aligns long-term ecosystem trust.
          </Typography>
        </Box>

        {/* Main horizontal content */}
        <Grid container spacing={6} alignItems="stretch">
          {/* Left: Pie card */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 6px 30px rgba(14,165,233,0.06)',
                overflow: 'hidden',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Token Allocation (by percentage)
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 220px', minWidth: 220, height: { xs: 220, md: 300 } }}>
                    <Pie
                      data={pieData}
                      options={{
                        plugins: { legend: { display: false } },
                        maintainAspectRatio: false,
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
                    <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary', mb: 1 }}>
                      Visual breakdown of DOLP allocation
                    </Typography>

                    <Stack spacing={1.25} sx={{ mt: 1 }}>
                      {allocations.map((a) => (
                        <Box key={a.label} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: 2,
                              backgroundColor: a.color,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{a.label}</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                              {a.amount.toLocaleString()} DOLP • {a.pct}%
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 0.5 }}>
                    Community
                  </Typography>
                  <Typography sx={{ fontWeight: 900, fontSize: '2rem', lineHeight: 1, color: '#0ea5e9' }}>
                    95%
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right: Table + progress */}
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: { xs: 2, md: 3 },
                  boxShadow: '0 6px 30px rgba(3,7,18,0.04)',
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 2 }}>Allocation Table</Typography>

                <TableContainer>
                  <Table size="small" aria-label="token allocation table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Allocation</TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="right">
                          Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="right">
                          %
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {allocations.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell component="th" scope="row">
                            {row.label}
                          </TableCell>
                          <TableCell align="right">{row.amount.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.pct}%</TableCell>
                        </TableRow>
                      ))}

                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Total Supply</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>
                          {totalSupply.toLocaleString()} DOLP
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>
                          100%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: { xs: 2, md: 3 },
                  boxShadow: '0 6px 30px rgba(3,7,18,0.04)',
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 2 }}>Allocation Progress</Typography>

                <Stack spacing={2}>
                  {allocations.map((item) => (
                    <Box key={item.label}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                        <Typography sx={{ fontWeight: 800, color: item.color }}>{item.pct}%</Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={item.pct}
                        sx={{
                          height: 12,
                          borderRadius: 999,
                          backgroundColor: theme.palette.mode === 'light' ? 'rgba(11,23,34,0.06)' : 'rgba(255,255,255,0.06)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: item.color,
                            borderRadius: 999,
                            transition: 'width 700ms ease',
                          },
                        }}
                        aria-label={`${item.label} allocation ${item.pct} percent`}
                      />
                    </Box>
                  ))}
                </Stack>
              </Paper>

              {/* Mining mechanism & principles — horizontal compact card */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: { xs: 2, md: 3 },
                  boxShadow: '0 6px 30px rgba(3,7,18,0.04)',
                }}
              >
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                      Mining Mechanism
                    </Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
                      Mining within Dolphin does not use hardware or energy. “Mining” means acquiring DOLP tokens
                      through ecosystem participation. Purchases are priced dynamically in USDT on Polygon.
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Key properties</Typography>
                    <Stack component="ul" spacing={0.6} sx={{ mt: 1, pl: 2 }}>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        Mining uses Polygon-supported USDT as the purchase currency.
                      </Typography>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        Token price increases linearly from start to end.
                      </Typography>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        Mining ends when the mining allocation (950,000,000 DOLP) is exhausted.
                      </Typography>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        All mined tokens are subject to vesting (see whitepaper for schedule).
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                      Key Principles
                    </Typography>
                    <Stack component="ul" spacing={0.6} sx={{ mt: 1, pl: 2 }}>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        <strong>No founder allocation</strong> — zero tokens reserved for founders outside community distribution.
                      </Typography>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        <strong>No private sale</strong> — no private pre-sale allocations to insiders.
                      </Typography>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        <strong>No team vesting allocation</strong> — team allocation is 0% to ensure community-first distribution.
                      </Typography>
                      <Typography component="li" sx={{ color: 'text.secondary' }}>
                        <strong>Community-first distribution</strong> — eliminates insider advantage and aligns long-term trust.
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
        {/* ── ROADMAP ── */}
        <Box id="roadmap" sx={{ py: { xs: 10, md: 14 }, backgroundColor: '#ffffff' }}>
          <Container maxWidth={false} disableGutters sx={SECTION_SX}>
            <Roadmap />
          </Container>
        </Box>

        {/* ── WHITEPAPER — dark CTA band ── */}
        {/* ── WHITEPAPER — dark CTA band ── */}
        <Box
          id="whitepaper"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #001a2c 0%, #003049 100%)',
            py: { xs: 10, md: 14 },
            '&::before': {
              content: '""',
              position: 'absolute', inset: 0,
              backgroundImage: `url("https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundSize: 'min(1200px, 90vw) auto',
              opacity: 0.05,
              filter: 'invert(1)',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth={false} disableGutters sx={{ ...SECTION_SX, position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: '#38bdf8', mb: 2,
            }}>
              Documentation
            </Typography>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2, color: '#ffffff',
            }}>
              Read the Whitepaper
            </Typography>
            <Typography sx={{
              color: 'rgba(255,255,255,0.6)', maxWidth: 480, mx: 'auto',
              lineHeight: 1.8, mb: 5, fontSize: '1rem',
            }}>
              Full protocol details, tokenomics breakdown, mining lifecycle mechanics, and long-term strategy.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                component="a"
                href="/whitepaper.pdf"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                sx={{
                  px: 4, py: 1.5, fontSize: '1rem',
                  backgroundColor: '#0ea5e9', color: '#001a2c',
                  '&:hover': { backgroundColor: '#38bdf8' },
                }}
              >
                Download Whitepaper (PDF)
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleConnectClick}
                sx={{
                  px: 4, py: 1.5, fontSize: '1rem',
                  borderColor: 'rgba(255,255,255,0.25)', color: '#ffffff',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
              >
                Start Mining
              </Button>
            </Stack>
          </Container>
        </Box>
        
      {/* ── FOOTER ── */}
<Box sx={{ backgroundColor: '#ffffff', borderTop: '1px solid rgba(11,23,34,0.07)' }}>
  <Container
    maxWidth={false}
    disableGutters
    sx={{ ...SECTION_SX, pt: { xs: 6, md: 8 }, pb: { xs: 6, md: 8 } }}
  >
    <Divider sx={{ my: { xs: 3, md: 4 } }} />

    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 4, md: 2 }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'center' }}
    >
      {/* Left side: copyright */}
      <Typography color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.82rem' }, textAlign: { xs: 'center', md: 'left' } }}>
        © {new Date().getFullYear()} Dolphin Ecosystem. All rights reserved.
      </Typography>

      {/* Middle: links */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 2 }}
        justifyContent="center"
        alignItems="center"
        sx={{ flexWrap: 'wrap', textAlign: 'center' }}
      >
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>Polygon Network</Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>·</Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>ERC-20</Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>·</Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>Audited</Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>support@dolphin.ecosystem</Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>DOLPHIN</Typography>
      </Stack>

      {/* Right side: logo */}
      <Box
        component="img"
        src={logo}
        alt="Dolphin"
        sx={{
          width: { xs: 40, md: 32 },
          height: { xs: 40, md: 32 },
          borderRadius: 1,
          mx: { xs: 'auto', md: 0 },
        }}
      />
    </Stack>
  </Container>
</Box>
</Box>

      <Button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        sx={{
          position: 'fixed', right: { xs: 12, md: 20 }, bottom: { xs: 12, md: 20 },
          zIndex: 1200, minWidth: 'unset', px: 2, py: 1,
          backgroundColor: '#003049', color: '#fff', borderRadius: 2,
          '&:hover': { backgroundColor: '#0ea5e9', color: '#001a2c' },
        }}
      >
        ↑
      </Button>

      <WalletSelectModal open={walletModalOpen && !account} onClose={() => setWalletModalOpen(false)} onSelect={handleWalletSelect} />
      <TermsModal
        open={termsOpen}
        onAccept={() => { setTermsOpen(false); setTosChecked(true); }}
        onClose={() => setTermsOpen(false)}
      />
    </ThemeProvider>
  );
}

export default LandingPage;